import * as React from 'react';
import { useEffect } from 'react';

import { DocumentSelectionWithUpload } from './DocumentSelectionWithUpload';
import { WizardStep } from './WizardStep';

interface WizardData {
  attachment_blob_ids?: string[];
  attachment_document_ids?: string[];
  [key: string]: unknown;
}

interface AttachmentSelectionStepProps {
  /**
   * Matter that the new cheque / letter belongs to. Used for scoping the blob list
   */
  matterId: string;
  /** Current wizard data for prefilling step (optional) */
  wizardData?: WizardData;
  /** Function provided by <Wizard/> for persisting step data */
  updateStepData?: (data: Partial<WizardData>) => void;
  /** Function provided by <Wizard/> for toggling step completion */
  markStepComplete?: (isValid: boolean) => void;
  /** Callback when a blob is uploaded - used for recipient extraction */
  onBlobUploaded?: (blobId: string) => void;
  /** When true the step is valid even with zero selections (used for optional attachments in LoR flow) */
  optional?: boolean;
  /** Override the step title */
  title?: string;
  /** Override the step description */
  description?: string;
  className?: string;
}

/**
 * Generic step for selecting existing uploaded documents (blobs) or uploading new ones
 * to attach to a cheque or letter. The caller can render this in any wizard and later
 * decide how to actually create the `PostgridAttachment` records once the parent
 * cheque / letter object exists.
 *
 * Behaviour:
 * • Fetches blobs for the provided matter
 * • Allows searching + multi-select via checkboxes
 * • Supports drag-and-drop file upload with real-time status
 * • Filters to show only PDF files by default
 * • Includes document preview functionality via InspectArtifactButton
 * • Persists the selected blob IDs in wizardData. The key used is
 *   `attachment_blob_ids` (string[]).
 * • Marks the step as complete only when at least one blob is selected.
 */
export const AttachmentSelectionStep: React.FC<
  AttachmentSelectionStepProps
> = ({
  matterId,
  wizardData = {},
  updateStepData,
  markStepComplete,
  onBlobUploaded,
  optional = false,
  title = 'Attachments',
  description = 'Select at least one PDF document you would like to include with this correspondence.',
  className = '',
}) => {
  // Initialise selection from wizardData (if user revisits the step)
  const initialSelection: Set<string> = React.useMemo(() => {
    const ids: string[] | undefined = wizardData.attachment_blob_ids;
    return new Set(ids ?? []);
  }, [wizardData]);

  const [selectedBlobIds, setSelectedBlobIds] =
    React.useState<Set<string>>(initialSelection);

  // Keep wizard state in-sync whenever selection changes
  // Require at least one attachment to proceed
  useEffect(() => {
    updateStepData?.({ attachment_blob_ids: Array.from(selectedBlobIds) });
    markStepComplete?.(optional || selectedBlobIds.size > 0);
  }, [selectedBlobIds, updateStepData, markStepComplete, optional]);

  // React setState handles both direct values and updater functions
  const handleSelectionChange = (
    newSelection: Set<string> | ((prev: Set<string>) => Set<string>),
  ) => {
    setSelectedBlobIds(newSelection);
  };

  return (
    <WizardStep
      title={title}
      description={description}
      isValid={optional || selectedBlobIds.size > 0}
      markStepComplete={markStepComplete}
      className={className}
    >
      <DocumentSelectionWithUpload
        matterId={matterId}
        selectedBlobIds={selectedBlobIds}
        onSelectionChange={handleSelectionChange}
        onBlobUploaded={onBlobUploaded}
        title=""
        description=""
      />
    </WizardStep>
  );
};
